import get from "lodash/get";
import set from "lodash/set";
import size from "lodash/size";
import omit from "lodash/omit";
import pick from "lodash/pick";
import chain from "lodash/chain";
import unset from "lodash/unset";
import forOwn from "lodash/forOwn";
import defaults from "lodash/defaults";

import { PRIMARIES } from "../constants";

function castEmptyToNull(val, key, obj) {
  if (val == null || val === "") obj[key] = null;
}

const CustomMetrics = (app) => {
  app
    // Updates the displayed chart.
    .post(
      "/custom-metrics/save/:customMetric?",
      async ({ req, res, api, flash, assert, locals }) => {
        const {
          body,
          params,
          query: { athlete },
        } = req;
        const activeUser = athlete ? locals.athlete : locals.activeUser;
        const { data, filters } = body;
        // Set empty value to null.
        forOwn(filters, castEmptyToNull);
        forOwn(get(filters, "intensity"), castEmptyToNull);
        forOwn(get(filters, "rpe"), castEmptyToNull);
        forOwn(data, castEmptyToNull);
        forOwn(data.line, castEmptyToNull);
        forOwn(data.bar, castEmptyToNull);

        // if the "included unassigned exercises" checkbox is not checked persist false to the db so we can ensure it's unchecked on edit
        // as the default is to have it checked on the front end
        if (!get(body, "filters.rpe.unassigned", false))
          set(body, "filters.rpe.unassigned", false);
        // if (chain(data).get("line").values().compact().isEmpty().value())
        //   data.line = null;
        // if (chain(data).get("bar").values().compact().isEmpty().value())
        //   data.bar = null;
        if (Object.values(data.line).every((value) => value === null)) {
          data.line = null;
        }

        if (Object.values(data.bar).every((value) => value === null)) {
          data.bar = null;
        }
        assert(
          get(data, "bar.series") || get(data, "line.series"),
          400,
          "You must select either a bar chart or at least one line chart."
        );
        assert(
          size(get(filters, "zones")),
          400,
          "You must select at least one zone of relative intensity."
        );

        if (get(data, "line.relation")) {
          assert(
            get(data, "line.series"),
            400,
            "You must select at least one line series."
          );
        }

        //filters come with string format. Here we convert them to objects again
        const secondaries = get(filters, "secondaries");
        if (secondaries) {
          set(filters, "secondaries", JSON.parse(secondaries));
        }

        if (get(filters, "type") === "secondary") {
          //if any type of primary exercise
          let primary = get(filters, "primary");
          if (primary) {
            // Default to all secondaries.
            let defaultSecondaries = {};
            defaultSecondaries["secondaries"] = {};
            defaultSecondaries["secondaries"][primary] =
              get(activeUser, `exerciseLibrary.${primary}`) || [];
            defaults(filters, defaultSecondaries);
          } else {
            let defaultSecondaries = {};
            defaultSecondaries["secondaries"] = omit(
              get(activeUser, `exerciseLibrary`),
              ["assistant"]
            );
            defaults(filters, defaultSecondaries);
          }

          const selectedExercisesSize = size(secondaries);
          assert(
            selectedExercisesSize >= 1,
            400,
            "You must select at least one secondary exercise to filter."
          );
        }

        const minIntensity = get(filters, "intensity.min");
        const maxIntensity = get(filters, "intensity.max");
        assert(
          minIntensity == null ||
            maxIntensity == null ||
            minIntensity < maxIntensity,
          400,
          "The minimum" + " intensity must be less than the maximum."
        );
        let savedID = params.customMetric;
        const isNew = !savedID;
        if (isNew) {
          const savedCustomMetric = await (
            await api(`custom-metric/?proxyAthlete=${athlete}`, {
              method: "POST",
              body: body,
            })
          ).json();

          savedID = savedCustomMetric._id;
          flash("success", "Custom report successfully created.");
        } else {
          await api(`custom-metric/${savedID}?proxyAthlete=${athlete}`, {
            method: "PUT",
            body: body,
          });
        }
        const redirectPath = athlete
          ? `/athlete-portal/metrics/custom/view/${savedID}?athlete=${athlete}`
          : `/dashboard/metrics/custom/view/${savedID}`;

        res.redirect(redirectPath);
      }
    )

    // Updates the secondary exercise list.
    .post(
      "/custom-metrics/save-secondaries/:primary/:customMetric?",
      async ({ req, res, flash, assert, load }) => {
        const {
          body,
          params,
          query: { athlete },
        } = req;
        const { primary, customMetric } = params;

        let selectedExercisesSize = 0;
        if (primary === "all") {
          //if all then iterate over all primary exercises and count selected exercises
          PRIMARIES.map((primaryExercise) => {
            selectedExercisesSize += size(
              get(body, `secondaries.${primaryExercise}`)
            );
          });
        } else {
          //if not all, count secondaries over the primary selected
          selectedExercisesSize = size(get(body, `secondaries.${primary}`));
        }
        assert(
          selectedExercisesSize >= 1,
          400,
          "You must select at least one secondary exercise."
        );

        const editCustomMetric = await load("editCustomMetric", {
          customMetric: customMetric,
          proxyAthlete: athlete,
        });

        if (primary === "all") {
          unset(editCustomMetric, "filters.primary");
        } else {
          set(editCustomMetric, "filters.primary", primary);
        }

        //convert secondaries from string to object again
        PRIMARIES.map((primaryExercise) => {
          if (body.secondaries[primaryExercise]) {
            body.secondaries[primaryExercise] = body.secondaries[
              primaryExercise
            ].map((sec) => {
              return JSON.parse(sec);
            });
          }
        });

        set(editCustomMetric, "filters.secondaries", body.secondaries);

        flash("success", "Secondary exercises filter updated.");

        res.refreshWith({ modal: "" }, { url: "back", hash: "#_" });
      }
    )

    // Updates the secondary exercise list.
    .get(
      "/custom-metrics/check-all-secondaries/:primary/:customMetric?",
      async ({ req, res, flash, assert, load, locals }) => {
        const {
          params,
          query: { athlete },
        } = req;
        const activeUser = athlete ? locals.athlete : locals.activeUser;
        const { primary, customMetric } = params;

        const editCustomMetric = await load("editCustomMetric", {
          customMetric: customMetric,
          proxyAthlete: athlete,
        });
        const exerciseLibrary = get(activeUser, "exerciseLibrary") || {};
        if (primary === "all") {
          unset(editCustomMetric, "filters.primary");
          set(
            editCustomMetric,
            "filters.secondaries",
            omit(exerciseLibrary, ["assistant"])
          );
        } else {
          set(editCustomMetric, "filters.primary", primary);
          set(
            editCustomMetric,
            "filters.secondaries",
            pick(exerciseLibrary, primary)
          );
        }

        res.backDontScroll();
      }
    )

    // Updates the secondary exercise list.
    .get(
      "/custom-metrics/uncheck-all-secondaries/:primary/:customMetric?",
      async ({ req, res, flash, assert, load, locals }) => {
        const {
          params,
          query: { athlete },
        } = req;
        const activeUser = athlete ? locals.athlete : locals.activeUser;

        const editCustomMetric = await load("editCustomMetric", {
          customMetric: params.customMetric,
          proxyAthlete: athlete,
        });

        if (params.primary === "all") {
          unset(editCustomMetric, "filters.primary");
        } else {
          set(editCustomMetric, "filters.primary", params.primary);
        }

        let emptySecondaries = {};
        PRIMARIES.map((primaryExercise) => {
          emptySecondaries[primaryExercise] = [];
        });
        set(editCustomMetric, "filters.secondaries", emptySecondaries);
        res.backDontScroll();
      }
    )

    // Remove an exercise
    .get(
      "/custom-metrics/remove/:customMetric",
      async ({ req, res, assert, flash, locals, api, load }) => {
        if (
          process.browser &&
          !confirm("Are you sure you want to delete this custom report?")
        ) {
          return res.backDontScroll();
        }

        const {
          params,
          query: { athlete },
        } = req;
        const activeUser = athlete ? locals.athlete : locals.activeUser;

        await load("editCustomMetric", {
          customMetric: params.customMetric,
          proxyAthlete: athlete,
        });
        const { editCustomMetric } = locals;
        assert(
          editCustomMetric,
          400,
          "Custom report has already been removed."
        );
        await api(
          `custom-metric/${editCustomMetric._id}?proxyAthlete=${athlete}`,
          {
            method: "DELETE",
          }
        );
        flash("success", "Custom report successfully removed.");
        res.backDontScroll();
      }
    );
};

export default CustomMetrics;
